// Shared plumbing for the Union web apps (Staff, member web, Ops):
// sign-in, the session, and calling the Union API.

import { createClient, type Session } from "@supabase/supabase-js";
import type { ApiError } from "@union/contracts";
import { useEffect, useRef, useState } from "react";

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
);

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly requestId: string,
    /** The API's plain-language explanation, when it gave one. */
    readonly detail = "",
  ) {
    super(`${status} ${code}`);
  }
}

/** The request never got an answer: offline, DNS, or the API is down. */
export class ApiUnreachableError extends Error {
  constructor(cause: unknown) {
    super("Couldn't reach the Union API", { cause });
  }
}

/** GET from the Union API as the signed-in user. Types come from @union/contracts. */
/** Anything carrying a bearer token: a Supabase session, or a club site's own session. */
export type Bearer = Pick<Session, "access_token">;

export async function apiGet<T>(path: string, session: Bearer): Promise<T> {
  const res = await fetch(new URL(path, import.meta.env.VITE_API_URL), {
    headers: { Authorization: `Bearer ${session.access_token}` },
  }).catch((cause: unknown) => {
    throw new ApiUnreachableError(cause);
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new ApiRequestError(res.status, body?.error.code ?? "unknown", body?.error.requestId ?? "");
  }
  return (await res.json()) as T;
}

/**
 * POST to the Union API with an Idempotency-Key. If the network drops before a
 * response arrives, retries ONCE with the same key, so the server returns the
 * original result instead of acting twice.
 */
/**
 * Sends a change to the API. With an `idempotencyKey` (send the same one on
 * retries), or for PUT/DELETE (which mean the same thing however often they're
 * sent), a dropped connection is retried once; a plain POST isn't, so a create
 * can't happen twice.
 */
export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  session: Bearer,
  options: { idempotencyKey?: string; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Bearer ${session.access_token}` };
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const send = () =>
    fetch(new URL(path, import.meta.env.VITE_API_URL), {
      method,
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
    });
  const retryable = method !== "POST" || !!options.idempotencyKey;
  const res = await send()
    .catch((cause: unknown) => (retryable ? send() : Promise.reject(cause)))
    .catch((cause: unknown) => {
      throw new ApiUnreachableError(cause);
    });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiError | null;
    throw new ApiRequestError(
      res.status,
      body?.error.code ?? "unknown",
      body?.error.requestId ?? "",
      body?.error.message,
    );
  }
  return (await res.json()) as T;
}

export const apiPost = <T,>(
  path: string,
  session: Bearer,
  options?: { idempotencyKey?: string; body?: unknown },
) => apiSend<T>("POST", path, session, options);

/**
 * Calls `onChange` whenever the club's tee sheet changes (any booking added,
 * changed or removed). The event carries IDs only; refetch through the API.
 * The channel is private: Supabase Realtime admits only the club's members
 * and staff (RLS on realtime.messages). Pass `accessToken` when the page isn't
 * signed in through Supabase Auth directly (club sites use their own session).
 */
export function useTeeSheetEvents(
  clubId: string | undefined,
  onChange: () => void,
  accessToken?: string,
): void {
  const latest = useRef(onChange);
  latest.current = onChange;
  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    // The database announces each changed row, so one action on many times (closing a day,
    // revealing a lottery, blocking a morning) sends a burst: refetch once, after it settles.
    let pending: ReturnType<typeof setTimeout> | undefined;
    const changed = () => {
      clearTimeout(pending);
      pending = setTimeout(() => latest.current(), 250);
    };
    const channel = supabase.channel(`club:${clubId}:tee-sheet`, { config: { private: true } });
    supabase.realtime.setAuth(accessToken).then(() => {
      if (cancelled) return;
      channel.on("broadcast", { event: "tee_sheet_changed" }, changed).subscribe();
    });
    return () => {
      cancelled = true;
      clearTimeout(pending);
      supabase.removeChannel(channel);
    };
  }, [clubId, accessToken]);
}

/** The current session; `undefined` while loading, `null` when signed out. */
export function useSession(): Session | null | undefined {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);
  return session;
}

export function signOut() {
  return supabase.auth.signOut();
}

/** Today's date (YYYY-MM-DD) in a club's timezone, never the viewer's. */
export function todayAt(timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date());
}
